import { api, type Prediction } from '../../lib/api';
import { useSelection } from '../../lib/selection';
import { periodRange, scopeLabel } from '../../lib/scopeKey';
import { SENTIMENTS, type Sentiment } from '../../lib/app';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 预测 —— 情绪 / 当时预期 / 实际发生，**与笔记、新闻同级的第三个 tab**。
 *
 * 为什么从笔记里拆出来：
 *  1. 这三项本质是**按日**的一条判断（情绪时间轴也按日聚合），
 *     挂在某一篇笔记上并不成立 —— 同一天可能有多篇笔记。
 *  2. 用户可能只想记「今天看涨」，不想为此写一篇笔记。
 *
 * 保存策略：**显式保存**，不做防抖自动保存（与笔记一致）。
 * 用户点「保存」才发请求；离开前若有改动会提示，避免误丢。
 */
export function PredictionPanel() {
  const { instrument, period, effectiveDate } = useSelection();
  // 预测按「日」存，故只取区间里的每一天；当前日期作为锚点
  const { from, to } = periodRange(period, effectiveDate ?? '');
  const activeDate = effectiveDate ?? from;

  const [pred, setPred] = useState<Prediction | null>(null);
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [expectation, setExpectation] = useState('');
  const [outcome, setOutcome] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const baseline = useRef('');
  const loadedFor = useRef('');

  const snapshot = `${sentiment ?? ''}|${expectation}|${outcome}`;

  /* ── 载入当前日期的预测 ── */
  useEffect(() => {
    if (!activeDate) return;
    const key = `${instrument.code}|${activeDate}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    void (async () => {
      try {
        const p = await api.prediction(instrument.code, activeDate);
        setPred(p);
        setSentiment((p.sentiment as Sentiment) ?? null);
        setExpectation(p.expectation);
        setOutcome(p.outcome);
        baseline.current = `${p.sentiment ?? ''}|${p.expectation}|${p.outcome}`;
        setError(null);
      } catch {
        setPred(null);
        setSentiment(null);
        setExpectation('');
        setOutcome('');
        baseline.current = '||';
        setError(null);
      }
    })();
  }, [instrument.code, activeDate]);

  const dirty = snapshot !== baseline.current;

  const save = useCallback(async () => {
    if (!activeDate || saving) return;
    setSaving(true);
    try {
      const p = await api.savePrediction(instrument.code, activeDate, {
        sentiment: sentiment ?? undefined, expectation, outcome,
      });
      setPred(p);
      baseline.current = `${p.sentiment ?? ''}|${p.expectation}|${p.outcome}`;
      setMsg('已保存');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [activeDate, saving, instrument.code, sentiment, expectation, outcome]);

  if (!activeDate) {
    return (
      <div className="p-4 text-[13px]" style={{ color: 'var(--text-3)' }}>
        请先选择一个交易日
      </div>
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b px-3">
        <span className="text-[12px] font-medium">◎ 预测</span>
        <span className="num truncate text-[11px]" style={{ color: 'var(--text-3)' }}>
          {instrument.name} · {activeDate}
        </span>
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {msg && !dirty && <span style={{ color: 'var(--down)' }}>{msg}</span>}
          {dirty && <span style={{ color: 'var(--warn)' }}>未保存</span>}
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="rounded px-2 py-0.5 text-[11px] disabled:opacity-40"
            style={{
              border: '1px solid var(--border)', color: dirty ? 'var(--accent)' : 'var(--text-3)',
            }}
          >
            {saving ? '保存中…' : '保存'}
          </button>
        </span>
      </div>

      {/* 区间提示：周/月/年下这一条预测只对应「当前选中的那一天」 */}
      {from !== to && (
        <div className="shrink-0 px-3 pt-2 text-[11px]" style={{ color: 'var(--text-3)' }}>
          当前编辑 {activeDate}（区间 {from} ~ {to} 内每天各一条）
        </div>
      )}

      <div className="shrink-0 px-3 pt-2">
        <div className="mb-1 text-[10px]" style={{ color: 'var(--text-3)' }}>情绪</div>
        <div className="flex items-center gap-1.5">
          {SENTIMENTS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSentiment(sentiment === s.id ? null : s.id)}
              aria-pressed={sentiment === s.id}
              className="rounded-full px-2.5 py-1 text-[12px]"
              style={{
                border: `1px solid ${sentiment === s.id ? `var(${s.varName})` : 'var(--border)'}`,
                color: sentiment === s.id ? `var(${s.varName})` : 'var(--text-3)',
              }}
            >{s.label}</button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 px-3 pt-3">
        <Field label="当时预期" value={expectation} onChange={setExpectation}
          placeholder="如：回踩不破 3800 则加仓" />
        <Field label="实际发生" value={outcome} onChange={setOutcome}
          placeholder="如：放量跌破 3760，未加仓" />
      </div>

      {error && (
        <div className="shrink-0 px-3 py-1 text-[11px]" style={{ color: 'var(--warn)' }}>⚠ {error}</div>
      )}

      <div className="shrink-0 px-3 pb-3 pt-1 text-[10px]" style={{ color: 'var(--text-3)' }}>
        {pred?.createdAt
          ? `创建于 ${pred.createdAt.slice(0, 16).replace('T', ' ')}`
          : '尚未记录预测'}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label className="mb-2 block">
      <span className="mb-1 block text-[10px]" style={{ color: 'var(--text-3)' }}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-[64px] w-full resize-y rounded-[6px] border p-2 text-[12px] leading-[1.6] outline-none"
        style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
      />
    </label>
  );
}

/** 供图表/其它视图复用：把区间里的预测取出来 */
export async function fetchRangePredictions(code: string, from: string, to: string) {
  const r = await api.predictions(code, from, to);
  return r.items;
}

export { scopeLabel };